import React, { useState, useEffect } from "react";
import "./RenwalLicense.scss";
import MainServices from "../../services/MainServices";
import axios from "axios";
import { LazyLoader } from "../../assets/loaders";
import { TbEye } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import Pagination from "../../assets/elements/Pagination";
import { FaSearch, FaIdCard, FaTimes, FaCopy } from "react-icons/fa";
import { IoMdCheckmarkCircle, IoMdTime, IoMdCloseCircle } from "react-icons/io";

const License = () => {
  const { getRenwalLicense, getRenwalLicenseById } = MainServices();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const [filteredLicenses, setFilteredLicenses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleRowClick = async (item) => {
    try {
      const result = await getRenwalLicenseById(item.id);
      if (result && result.success) {
        setSelectedLicense(result.data);
        setShowModal(true);
      }
    } catch (error) {
      console.error("Error fetching license details:", error);
    }
  };

  const LicenseDetailModal = ({ data, onClose }) => {
    if (!data) return null;

    const getStatusStyle = (status) => {
      const statusLower = status?.toLowerCase();
      if (statusLower === "success") return { bg: "#d1e7dd", color: "#0f5132" };
      if (statusLower === "ongoing") return { bg: "#fff3cd", color: "#856404" };
      if (statusLower === "abandoned") return { bg: "#f8d7da", color: "#842029" };
      return { bg: "#e9ecef", color: "#495057" };
    };

    const statusStyle = getStatusStyle(data.payment_status?.response?.pay_status);

    const copyToClipboard = (text) => {
      navigator.clipboard.writeText(text);
      alert("Payment link copied!");
    };

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content-wrapper" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header-custom">
            <h2>
              <FaIdCard /> License Renewal Details
            </h2>
            <button className="modal-close-btn" onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          {/* Body */}
          <div className="modal-body-custom">
            {/* User */}
            <div className="modal-field">
              <label>User</label>
              <div className="modal-value">{data.user?.name || "-"}</div>
            </div>

            {/* Email */}
            <div className="modal-field">
              <label>Email</label>
              <div className="modal-value">{data.user?.email || "-"}</div>
            </div>

            {/* Vehicle Reg */}
            <div className="modal-field">
              <label>Vehicle Registration</label>
              <div className="modal-value-highlight">
                {data.response?.response?.vehicle_registration || "-"}
              </div>
            </div>

            {/* Renewal ID */}
            <div className="modal-field">
              <label>Renewal ID</label>
              <div className="modal-value-code">
                {data.response?.response?.licence_renewal_id || "-"}
              </div>
            </div>

            {/* Total Amount */}
            <div className="modal-field">
              <label>Total Amount</label>
              <div className="modal-value-amount">
                R {data.response?.response?.Total?.toFixed(2) || "0.00"}
              </div>
            </div>

            {/* Payment Status */}
            <div className="modal-field">
              <label>Payment Status</label>
              <div>
                <span
                  className="modal-status-badge"
                  style={{
                    backgroundColor: statusStyle.bg,
                    color: statusStyle.color,
                  }}
                >
                  {data.payment_status?.response?.pay_status?.toUpperCase() || "UNKNOWN"}
                </span>
              </div>
            </div>

            {/* Payment Link */}
            <div className="modal-field-full">
              <label>Payment Link</label>
              {data.response?.response?.paylink ? (
                <div className="modal-paylink">
                  <input
                    type="text"
                    readOnly
                    value={data.response.response.paylink}
                    className="modal-paylink-input"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(data.response.response.paylink);
                    }}
                    className="modal-copy-btn"
                  >
                    <FaCopy /> Copy
                  </button>
                </div>
              ) : (
                <div className="modal-value-muted">Not available</div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer-custom">
            <button className="modal-btn-close" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const actions = (row) => {
    if (!row) return null;
    return (
      <div className="action-btn-main">
        <div
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          title="View Details"
        >
          <TbEye size={22} color="#005478" />
        </div>
      </div>
    );
  };

  const getServices = async () => {
    setLoading(true);
    try {
      const res = await getRenwalLicense(1, 1000);

      if (res && res.success) {
        const resData = res.data || {};

        if (resData.results && Array.isArray(resData.results)) {
          setLicenses(resData.results);
          setFilteredLicenses(resData.results);
        } else {
          setLicenses([]);
          setFilteredLicenses([]);
        }
      }
    } catch (error) {
      console.error("Error fetching license renewals:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getServices();
  }, []);

  useEffect(() => {
    let filtered = licenses;

    if (statusFilter !== "all") {
      filtered = filtered.filter((license) => {
        const status = license.payment_status?.response?.pay_status?.toLowerCase() || "";
        return status === statusFilter.toLowerCase();
      });
    }

    if (searchTerm !== "") {
      filtered = filtered.filter(
        (license) =>
          license.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          license.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          license.response?.response?.vehicle_registration
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          license.response?.response?.licence_renewal_id
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    setFilteredLicenses(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, licenses]);

  // Local pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLicenses = filteredLicenses.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLicenses.length / itemsPerPage);

  const statusStyles = {
    success: {
      backgroundColor: "#d1e7dd",
      color: "#0f5132",
      icon: IoMdCheckmarkCircle,
    },
    ongoing: {
      backgroundColor: "#fff3cd",
      color: "#856404",
      icon: IoMdTime,
    },
    abandoned: {
      backgroundColor: "#f8d7da",
      color: "#842029",
      icon: IoMdCloseCircle,
    },
  };

  const getStatusInfo = (paymentStatus) => {
    const status = paymentStatus?.response?.pay_status?.toLowerCase() || "";
    if (status === "success") return { text: "Success", style: statusStyles.success };
    if (status === "ongoing") return { text: "Ongoing", style: statusStyles.ongoing };
    if (status === "abandoned") return { text: "Abandoned", style: statusStyles.abandoned };
    return { text: status || "Unknown", style: { backgroundColor: "#e9ecef", color: "#495057" } };
  };

  const formatCurrency = (amount) => {
    if (!amount) return "R 0.00";
    return `R ${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusCounts = () => {
    return {
      all: licenses.length,
      success: licenses.filter((l) => l.payment_status?.response?.pay_status === "success").length,
      ongoing: licenses.filter((l) => l.payment_status?.response?.pay_status === "ongoing").length,
      abandoned: licenses.filter((l) => l.payment_status?.response?.pay_status === "abandoned")
        .length,
    };
  };

  const getTotalAmount = () => {
    const total = licenses.reduce((sum, license) => {
      return sum + (parseFloat(license.response?.response?.Total) || 0);
    }, 0);
    return formatCurrency(total);
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="main-license-container">
      <div className="header-section">
        <div className="title-section">
          <h4>
            <FaIdCard /> License Renewals
          </h4>
          <p className="subtitle">Manage and track all vehicle license renewal applications</p>
        </div>
        <div className="stats-cards">
          <div className="stat-card total">
            <span className="stat-value">{statusCounts.all}</span>
            <span className="stat-label">Total Renewals</span>
          </div>
          <div className="stat-card amount">
            <span className="stat-value">{getTotalAmount()}</span>
            <span className="stat-label">Total Amount</span>
          </div>
          <div className="stat-card success">
            <span className="stat-value">{statusCounts.success}</span>
            <span className="stat-label">Paid</span>
          </div>
          <div className="stat-card ongoing">
            <span className="stat-value">{statusCounts.ongoing}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div className="stat-card abandoned">
            <span className="stat-value">{statusCounts.abandoned}</span>
            <span className="stat-label">Abandoned</span>
          </div>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, email, vehicle reg, renewal ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm("")} className="clear-btn">
              ✕
            </button>
          )}
        </div>

        <div className="status-filters">
          <button
            className={`filter-btn ${statusFilter === "all" ? "active" : ""}`}
            onClick={() => setStatusFilter("all")}
          >
            All ({statusCounts.all})
          </button>
          <button
            className={`filter-btn success ${statusFilter === "success" ? "active" : ""}`}
            onClick={() => setStatusFilter("success")}
          >
            Paid ({statusCounts.success})
          </button>
          <button
            className={`filter-btn ongoing ${statusFilter === "ongoing" ? "active" : ""}`}
            onClick={() => setStatusFilter("ongoing")}
          >
            Pending ({statusCounts.ongoing})
          </button>
          <button
            className={`filter-btn abandoned ${statusFilter === "abandoned" ? "active" : ""}`}
            onClick={() => setStatusFilter("abandoned")}
          >
            Abandoned ({statusCounts.abandoned})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loader-container">
          <LazyLoader />
        </div>
      ) : (
        <div className="license-table-container">
          {filteredLicenses && filteredLicenses.length > 0 ? (
            <>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User Name</th>
                    <th>Email</th>
                    <th>Vehicle Reg</th>
                    <th>Renewal ID</th>
                    <th>Amount</th>
                    <th className="text-center">Status</th>
                    <th>Created Date</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentLicenses.map((item, index) => {
                    const statusInfo = getStatusInfo(item.payment_status);
                    const StatusIcon = statusInfo.style.icon;

                    return (
                      <tr
                        key={item?.id || index}
                        className="tr-table"
                        onClick={() => handleRowClick(item)}
                      >
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td>
                          <strong>{item.user?.name || "-"}</strong>
                        </td>
                        <td>{item.user?.email || "-"}</td>
                        <td>{item.response?.response?.vehicle_registration || "-"}</td>
                        <td className="code-text">
                          {item.response?.response?.licence_renewal_id || "-"}
                        </td>
                        <td>
                          <strong className="amount-text">
                            {formatCurrency(item.response?.response?.Total)}
                          </strong>
                        </td>
                        <td className="text-center">
                          <div
                            className="status-badge"
                            style={{
                              backgroundColor: statusInfo.style.backgroundColor,
                              color: statusInfo.style.color,
                            }}
                          >
                            {StatusIcon && <StatusIcon size={14} />}
                            <span>{statusInfo.text}</span>
                          </div>
                        </td>
                        <td>{formatDate(item.created_at)}</td>
                        <td className="text-center">{actions(item)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                paginate={setCurrentPage}
              />
            </>
          ) : (
            <div className="no-license-div">
              <FaIdCard size={48} />
              <h5>
                {searchTerm || statusFilter !== "all"
                  ? `No license renewals found matching your filters`
                  : "No License Renewals found!"}
              </h5>
              {(searchTerm || statusFilter !== "all") && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                  }}
                  className="reset-btn"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {showModal && (
        <LicenseDetailModal data={selectedLicense} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
};

export default License;